// THE MACHINE-FIRST MODEL CALL (#6759, epic #6552) — the closed vocabulary every reader of
// a `model_call_jobs` row, a model-call task envelope and the two `model_call.*` audit rows
// shares. #6760 adds the runner half, the worker responder and the dead-caller sweep (the
// first `cluster / caller_gone` settle); #6762 and #6763 are the first callers.
//
// A LEAF (zod and the ids leaf only), because the audit payloads import it and the
// audit vocabulary sits under barrels that reach back into it (`entities/audit/payload.ts`
// says why a barrel import there is a TDZ cycle).

import { z } from 'zod';
import { ModelCallJobIdSchema } from './ids';

/**
 * Which caller's call a job is: the stored `model_call_jobs.kind`, CHECKed in SQL. The same
 * spelling as the task origin the offer mints, so one name answers "whose call" on the row,
 * the envelope and the audit trail; `model-call.test.ts` pins every member to a
 * `TaskOrigin`. Spelled as literals, the declaration shape `scripts/closed-set-release.ts`
 * reads as an enumeration rather than a write of the origins it names (`plan_author`, #7569).
 */
export const ModelCallKindSchema = z.enum(['triage_judge', 'kb_fold', 'plan_author']);
export type ModelCallKind = z.infer<typeof ModelCallKindSchema>;

/**
 * What a model-call task envelope carries (`TaskEnvelope.model_call`), the way `pr_review`
 * carries `review`: the job whose prompt the machine fetches. The prompt itself never rides
 * the envelope; it stays in Postgres until the offer settles.
 */
export const ModelCallTaskSpecSchema = z.object({ job_id: ModelCallJobIdSchema });
export type ModelCallTaskSpec = z.infer<typeof ModelCallTaskSpecSchema>;

/**
 * The cluster's EXACT pick for this one call (`model_call_jobs.model_pin`): the key the
 * caller was admitted on and the model it chose. The credential is sealed to it, so there
 * is no failover key (`ORIGIN_READS_LLM_FALLBACK` is false for both origins).
 */
export const ModelCallPinSchema = z
  .object({
    key_id: z.string().min(1),
    provider: z.string().min(1),
    model: z.string().min(1),
  })
  .strict();
export type ModelCallPin = z.infer<typeof ModelCallPinSchema>;

/** Who answered the call: the machine that claimed the offer, or the cluster that took it back. */
export const ModelCallExecutor = { Machine: 'machine', Cluster: 'cluster' } as const;
export const ModelCallExecutorSchema = z.enum(ModelCallExecutor);
export type ModelCallExecutor = z.infer<typeof ModelCallExecutorSchema>;

/** Why the CLUSTER ran a call that was meant for a machine. */
export const ModelCallClusterReason = {
  /** No idle machine the account owns was eligible, so no offer was made. */
  NoEligibleMachine: 'no_eligible_machine',
  /** The served prompt would exceed the wire's limit, so no offer was made. */
  PromptOverWireLimit: 'prompt_over_wire_limit',
  /** The caller named a `schema_key` the registry does not hold. */
  UnregisteredSchema: 'unregistered_schema',
  /** No eligible machine claimed the offer inside its window (`machine_unclaimed` cancel). */
  MachineUnclaimed: 'machine_unclaimed',
  /** The machine that claimed it did not answer by `deadline_at` (`machine_deadline` cancel). */
  MachineDeadline: 'machine_deadline',
  /** The machine's result failed its schema, or the box reported an error. */
  MachineRefused: 'machine_refused',
  /** The sweep settled an offer whose caller died; the caller's redelivery judges again. */
  CallerGone: 'caller_gone',
} as const;
export const ModelCallClusterReasonSchema = z.enum(ModelCallClusterReason);
export type ModelCallClusterReason = z.infer<typeof ModelCallClusterReasonSchema>;

/** The one reason a MACHINE settles a call with. */
export const MODEL_CALL_MACHINE_REASON = 'answered' as const;

/** Every `model_call_jobs.settle_reason` a reader may meet. */
export const ModelCallSettleReasonSchema = z.enum([
  MODEL_CALL_MACHINE_REASON,
  ...ModelCallClusterReasonSchema.options,
]);
export type ModelCallSettleReason = z.infer<typeof ModelCallSettleReasonSchema>;

/**
 * Did a claim's credential resolution land EXACTLY the job's pin (#6760)? The bound seal of
 * a model-call claim: the same key, the same provider and the same model, compared exactly
 * (never `sameModelId`'s gateway-prefix folding: the pin is the cluster's own pick, so any
 * difference is a different call). `null` (nothing resolved) never lands. The precedent is
 * `escalatedResolutionLanded`.
 */
export function modelPinLanded(
  pin: ModelCallPin,
  landed: { readonly key_id: string; readonly provider: string; readonly model: string } | null,
): boolean {
  return (
    landed !== null &&
    landed.key_id === pin.key_id &&
    landed.provider === pin.provider &&
    landed.model === pin.model
  );
}

/**
 * The seven fields a `model_call_prompt` reply serves (`@developerz/nats`
 * `FleetModelCallPromptServed`), structurally: this leaf does not depend on the wire
 * package, and the wire type is assignable to this one.
 */
export type ModelCallServedFields = {
  readonly job_id: string;
  readonly lease_epoch: number;
  readonly schema_key: string;
  readonly model_pin: ModelCallPin;
  readonly system: string;
  readonly prompt: string;
  readonly deadline_at: string;
};

const SERVED_BYTES_ENCODER = new TextEncoder();

/**
 * THE ONE MEASURE of a served model call's size (#6762): the UTF-8 byte length of
 * `JSON.stringify` over exactly the seven served fields, as the codec writes the reply.
 * Both sides of the wire limit read it — the responder refusing an over-limit reply
 * (`apps/worker/src/fleet/model-call-rpc.ts`) and the caller deciding whether to offer at
 * all (`apps/agents-manager/src/triage-machine-first.ts`) — so the producer and the
 * responder cannot disagree about whether a call fits. Bytes, never characters: JSON
 * escaping and multi-byte characters both grow a prompt on the wire. `TextEncoder`, not
 * `Buffer`, because this leaf carries no Node dependency; the two count the same.
 */
export function modelCallServedBytes(served: ModelCallServedFields): number {
  return SERVED_BYTES_ENCODER.encode(
    JSON.stringify({
      job_id: served.job_id,
      lease_epoch: served.lease_epoch,
      schema_key: served.schema_key,
      model_pin: served.model_pin,
      system: served.system,
      prompt: served.prompt,
      deadline_at: served.deadline_at,
    }),
  ).length;
}
